import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'

import PageContainer from '@/components/ui/containers/PageContainer'
import TextInput from '@/components/ui/inputs/TextInput'

import { useCreateApiKeyMutation } from '@/core/api'
import { useGetPoliciesQuery } from '@/core/services/policy'

import { getApiErrorMessage } from '@/utils/errors'

import ApiKeyForm from './components/ApiKeyForm'

export function CreateApiKeyPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [apiSecretKey, setApiSecretkey] = useState<string>()

  const { data: policies } = useGetPoliciesQuery()
  const [createApiKey, { isLoading: isCreatingAccountGroup }] = useCreateApiKeyMutation()

  const policiesData = useMemo(
    () =>
      policies?.map((policy) => ({
        id: policy._id,
        name: policy.name,
        description: policy.description,
      })),
    [policies]
  )
  return (
    <Box>
      <Container maxWidth="xl">
        <Stack spacing={1} sx={{ pl: '30px', pr: '30px' }}>
          <Typography variant="h2" sx={{ mt: '30px', mb: '30px' }}>
            {t('page.createApiKey.title')}
          </Typography>
        </Stack>
      </Container>
      <PageContainer sx={{ '& > div': { paddingTop: 0 } }}>
        <ApiKeyForm
          isSubmitting={isCreatingAccountGroup}
          onSubmit={async (values) => {
            try {
              const { apiKey } = await createApiKey({ name: values.name, policies: values.policies }).unwrap()
              setApiSecretkey(apiKey)
            } catch (error) {
              enqueueSnackbar(getApiErrorMessage(error, t('notification.apiKey.create.error')), { variant: 'error' })
            }
          }}
          policies={policiesData}
        />
      </PageContainer>
      <Dialog onClose={() => setApiSecretkey(undefined)} open={!!apiSecretKey} PaperProps={{ sx: { maxWidth: 'sm', width: '100%' } }}>
        <DialogTitle>{t('dialog.apiSecretKey.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Typography>{t('dialog.apiSecretKey.message')}</Typography>
            <Stack direction="row" spacing={2}>
              <TextInput fullWidth value={apiSecretKey} />
              <Button
                onClick={() => {
                  if (apiSecretKey) {
                    navigator.clipboard.writeText(apiSecretKey)
                    enqueueSnackbar(t('notification.apiKey.copy.success'), { variant: 'success' })
                  }
                }}
                variant="contained"
              >
                <ContentCopyRoundedIcon />
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApiSecretkey(undefined)
              navigate(location.pathname.replace('/create', ''))
            }}
          >
            {t('common:button.done.label')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CreateApiKeyPage
